import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { setupAPIs } from "../setupUtils";
import { saveAs } from "file-saver";

const initialCustomerGroup = {
	isEdit: true,
	isLoading: false,

	countries: [],
	country: "",
	countryID: "",

	customerLevels: [],
	customerLevel: "",
	customerLevelID: "",

	customerTypes: [],
	customerType: "",
	customerTypeCode: "",

	customerGroups: [],
	customerGroupName: "",
	customerGroupID: "",
	customerGroupDetail: null,

	relShipLevels: [],
	relShipLevel: "All",
	relShipLevelID: 0,

	customerName: "",

	activeCustomer: true,
	newCustomer: false,

	customers: [],
	selCustomers: [],

	files: [],

	editCGName: "",

	editFromDate: dayjs(),
	editToDate: dayjs("2099-12-31"),

	status: "None",

	createCGName: "",

	createFromDate: dayjs().add(1, "day"),
	createToDate: dayjs("2099-12-31"),
	createIndDateUpdated: false,
	editIndDateUpdated: false,
	createEditStartEndDateModalOpened: false,

	popUpMsg: "",
	popUpSeverity: "",
	popUpOpen: false,

	response: null,

	createComments: "",
	editComments: "",
	isNavigate: false,
	selectedCL: "",
	selectedCLID: "",

	customerCountries: [],
  selCustCountries: [],
};

async function apiCall({ url, method = "GET", body }) {
	try {
		const headers = {
			"Content-Type": "application/json",
			Accept: "application/json",
		};

		const d = JSON.parse(localStorage.getItem("data"));
		const token = d.token;

		if (token) {
			headers.Authorization = `Bearer ${token}`;
		}

		const options = {
			method,
			headers,
		};

		if (body) {
			options.body = JSON.stringify(body);
		}

		const res = await fetch(url, options);
		const data = await res.json();
		return data;
	} catch (error) {
		console.error("API Error", url, error);
		throw error;
	}
}

export const getUserCountries = createAsyncThunk(
	"customerGroup/getUserCountries",
	async (obj, thunkAPI) => {
		const data = JSON.parse(localStorage.getItem("data"));
		const url = setupAPIs.getUserCountries + `userid=${data.userID}`;
		const res = await apiCall({ url });
		return res;
	}
);

export const getCustomerCountries = createAsyncThunk(
	"customerGroup/getCustomerCountries",
	async (obj, thunkAPI) => {
		const data = JSON.parse(localStorage.getItem("data"));
		const { CountryId, Level, Type } = obj;
		const url =
			setupAPIs.getCustomerCountries +
			`CountryId=${CountryId}&Level=${Level}&Type=${Type}`;
		const res = await apiCall({ url });
		return res;
	}
);

export const getCGRefData = createAsyncThunk(
	"customerGroup/getCGRefData",
	async (obj, thunkAPI) => {
		const { countryID } = obj;
		const url = setupAPIs.getCGRefData + `countryId=${countryID}`;
		const data = await apiCall({ url });
		return data;
	}
);

export const getCGForCountry = createAsyncThunk(
	"customerGroup/getCGForCountry",
	async ({ countryID, customerTypeCode, customerGroupName }, thunkAPI) => {
		const url =
			setupAPIs.getCGForCountry +
			`CountryID=${countryID}&CustomerTypeCode=${customerTypeCode}&CustomerGroupName=${customerGroupName}`;
		const data = await apiCall({ url });
		return data;
	}
);

export const getCGDetail = createAsyncThunk(
	"customerGroup/getCGDetail",
	async ({ customerGroupID }, thunkAPI) => {
		const url = setupAPIs.getCGDetail + `CustomerGroupID=${customerGroupID}`;
		const data = await apiCall({ url });
		return data;
	}
);

export const getRelshipLevel = createAsyncThunk(
	"customerGroup/getRelshipLevel",
	async (obj, thunkAPI) => {
		const url = setupAPIs.getRelshipLevel;
		const data = await apiCall({ url });
		return data;
	}
);

export const getCustomers = createAsyncThunk(
	"customerGroup/getCustomers",
	async (
		{
			countryID,
			customerTypeCode,
			customerLevelID,
			customerName,
			activeCustomer,
			relShipLevelID,
			newCustomer,
			customerCountries,
			category,
		},
		thunkAPI
	) => {
		const url = setupAPIs.getCustomers;
		let body = {
			countryID,
			cstTypeCode: customerTypeCode,
			cstLvlCode: customerLevelID,
			cstName: customerName,
			activeCst: activeCustomer,
			trstLvl: relShipLevelID,
			newCustomer: newCustomer ? 3 : 0,
      // in the API the spelling is selectedCustomerCounries
			selectedCustomerCounries: customerCountries,
			category,
		};
    console.log(body);

		const data = await apiCall({ url, method: "POST", body });
		return data;
	}
);

export const submitCustomerGroup = createAsyncThunk(
	"customerGroup/submitCustomerGroup",
	async (obj, thunkAPI) => {
		const url = setupAPIs.submitCustomerGroup;
		const data = await apiCall({ url, method: "POST", body: obj });
		return data;
	}
);

export const saveCustomerGroup = createAsyncThunk(
	"customerGroup/saveCustomerGroup",
	async (obj, thunkAPI) => {
		const url = setupAPIs.saveCustomerGroup;
		const data = await apiCall({ url, method: "POST", body: obj });
		return data;
	}
);

export const cloneCustomerGroup = createAsyncThunk(
	"customerGroup/cloneCustomerGroup",
	async (obj, thunkAPI) => {
		const url = setupAPIs.cloneCustomerGroup;
		const data = await apiCall({ url, method: "POST", body: obj });
		return data;
	}
);

export const uploadFileForCustomerGroup = createAsyncThunk(
	"customerGroup/uploadFileForCustomerGroup",
	async (formdata, thunkAPI) => {
		const data = JSON.parse(localStorage.getItem("data"));
		const token = data.token;
		const url = setupAPIs.uploadFileForCustomerGroup;
		try {
			const response = await fetch(url, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					Accept: "application/json",
				},
				body: formdata,
			});
			if (response.status === 204) {
				return {};
			}
			const jsonData = await response.json();
			return jsonData;
		} catch (error) {
			console.error("Error: ", error);
		}
	}
);

export const downloadAttachment = createAsyncThunk(
	"customerGroup/downloadAttachment",
	async (obj, thunkAPI) => {
		const data = JSON.parse(localStorage.getItem("data"));
		const token = data.token;
		const url = setupAPIs.downloadAttachment + `fileName=${obj.fileName}`;
		try {
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
					Accept: "application/json",
				},
			});
			const res = await response.blob();
			const fileName = `${obj.fileName}`;
			saveAs(res, fileName);
			return res;
		} catch (error) {
			console.error("Error: ", error);
		}
	}
);

const customerGroupSetupSlice = createSlice({
	name: "customerGroupSetup",
	initialState: initialCustomerGroup,
	reducers: {
		setCGState: {
			prepare(stateName, stateValue) {
				return {
					payload: { stateName, stateValue },
				};
			},
			reducer(state, action) {
				const { stateName, stateValue } = action.payload;
				state[stateName] = stateValue;
			},
		},
		resetAllStates(state, action) {
			if (action.payload.edit) {
				state.isEdit = true;
			}
			//state.isLoading = false;
			if (!state.isNavigate) {
				state.country = "";
				state.countryID = "";
			}
			state.isNavigate = false;
			state.customerType = "";
			state.customerTypeCode = "";

			// state.customerLevels = [];
			state.customerLevel = "";
			state.customerLevelID = "";
			state.selectedCL = "";
			state.selectedCLID = "";

			// state.customerTypes = [];

			state.customerGroups = [];
			state.editComments = "";
			state.customerGroupName = "";
			state.customerGroupID = "";
			state.customerGroupDetail = null;

			// state.relShipLevels = [];
			state.relShipLevel = "All";
			state.relShipLevelID = 0;

			state.customerName = "";

			state.activeCustomer = true;
			state.newCustomer = false;

			state.customers = [];
			state.selCustomers = [];

			state.files = [];
			state.editCGName = "";
			state.editFromDate = dayjs().add(1, "day");
			state.editToDate = dayjs("2099-12-31T00:00:00.000Z");
			state.status = "None";
			state.createCGName = "";
			state.createFromDate = dayjs().add(1, "day");
			state.createToDate = dayjs("2099-12-31T00:00:00.000Z");
			state.createIndDateUpdated = false;
			state.editIndDateUpdated = false;
			state.createEditStartEndDateModalOpened = false;
			state.response = null;
			//state.popUpMsg = "";
			//state.popUpSeverity = "";
			//state.popUpOpen = false;
			state.popUpMsg = "";
			state.popUpSeverity = "";
			state.popUpOpen = false;
			state.createComments = "";

      state.customerCountries = [];
      state.selCustCountries = [];
		},
		addFile(state, action) {
			//  console.log(action.payload);
			const updatedFiles = [...state.files, action.payload];
			//  console.log(updatedFiles);
			state.files = updatedFiles;
		},
		deleteFile(state, action) {
			const { filePath } = action.payload;
			const normalArray = [...state.files];
			let updatedFiles = [];
			if (filePath === undefined) {
				const { name } = action.payload;
				updatedFiles = normalArray.filter((file) => file.name !== name);
			} else {
				updatedFiles = state.files.filter((file) => file.filePath !== filePath);
			}
			state.files = updatedFiles;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getUserCountries.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getUserCountries.fulfilled, (state, action) => {
				state.isLoading = false;
				state.countries = action.payload;
			})
			.addCase(getUserCountries.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from GetUserCountries", action.error.message);
			})

			.addCase(getCustomerCountries.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getCustomerCountries.fulfilled, (state, action) => {
				state.isLoading = false;
				// console.log(action.payload);
				state.customerCountries = action.payload;
			})
			.addCase(getCustomerCountries.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from GetUserCountries", action.error.message);
			})

			.addCase(getCGRefData.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getCGRefData.fulfilled, (state, action) => {
				state.isLoading = false;
				state.customerLevels = action.payload.customerLevels;
				state.customerTypes = action.payload.customerTypes;
				const idx = state.customerLevels.findIndex(
					(custLevel) => custLevel.customerLevelID === "SH"
				);
				state.selectedCL = state.customerLevels[idx].customerLevel;
        state.customerLevel = state.customerLevels[idx].customerLevel;
				state.customerLevelID = state.customerLevels[idx].customerLevelID;
				state.selectedCLID = state.customerLevels[idx].customerLevelID;
			})
			.addCase(getCGRefData.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from getCGRefData", action.error.message);
			})

			.addCase(getCGForCountry.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getCGForCountry.fulfilled, (state, action) => {
				state.isLoading = false;
				state.customerGroups = action.payload;
				if (action.payload.length === 0) {
					state.popUpMsg = "No customer group found.";
					state.popUpSeverity = "info";
					state.popUpOpen = true;
				}
			})
			.addCase(getCGForCountry.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from getCGForCountry", action.error.message);
			})

			.addCase(getCGDetail.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getCGDetail.fulfilled, (state, action) => {
				state.isLoading = false;
				console.log(
					"The CG details inside cgsetupslice for getCGDetail api call are",
					action.payload
				);
				state.customerGroupDetail = action.payload;
				//if(state.isNavigate===true){
				state.customerTypeCode =
					state.customerGroupDetail.customerGroupTypeCode;
				state.customerType = state.customerTypes.find(
					(cType) => cType.customerTypeCode === state.customerTypeCode
				).customerTypeName;
				//}
			})
			.addCase(getCGDetail.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from getCGDetail", action.error.message);
			})

			.addCase(getRelshipLevel.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getRelshipLevel.fulfilled, (state, action) => {
				state.isLoading = false;
				state.relShipLevels = action.payload;
			})
			.addCase(getRelshipLevel.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from getRelshipLevel", action.error.message);
			})

			.addCase(getCustomers.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(getCustomers.fulfilled, (state, action) => {
				state.isLoading = false;
				if (action.payload.length !== 0) {
					if (state.selCustomers.length === 0) {
						state.customers = action.payload;
					} else {
						let filteredList = [];
						let list = action.payload;
						for (let i = 0; i < list.length; ++i) {
							const customer = list[i];
							let item = state.selCustomers.find(
								(item) => item.customerID === customer.customerID
							);
							if(state.customerLevelID === 'SH'){
								if(!item){
									item = state.selCustomers.find(
										(item) => item.sapCustomerNumber === customer.sapCustomerNumber
									);
								}
							}
							if (!item) {
								filteredList.push(customer);
							}
						}
						state.customers = filteredList;
					}
				} else {
					if (state.isEdit === true) {
						state.customers = [];
						state.popUpMsg = "No customers found.";
						state.popUpSeverity = "info";
						state.popUpOpen = true;
					} else {
						state.customers = [];
						state.popUpMsg = "No customers found.";
						state.popUpSeverity = "info";
						state.popUpOpen = true;
					}
				}
			})
			.addCase(getCustomers.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from getCustomers", action.error.message);
			})

			.addCase(submitCustomerGroup.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(submitCustomerGroup.fulfilled, (state, action) => {
				state.response = action.payload;
				if (state.isEdit === false) {
					if (typeof state.response === "string") {
						state.popUpOpen = true;
						state.popUpMsg = state.response;
						state.popUpSeverity = "error";
					} else if (typeof state.response === "object") {
						state.popUpOpen = true;
						state.popUpMsg = "Customer group submitted successfully";
						state.popUpSeverity = "success";
					} else {
						state.popUpOpen = true;
						state.popUpMsg = "Threr is some error in submitting customer group";
						state.popUpSeverity = "error";
					}
					//state.popUpOpen = true;
					state.isLoading = false;
				} else {
					if (typeof state.response === "string") {
						state.popUpOpen = true;
						state.popUpMsg = state.response;
						state.popUpSeverity = "error";
					} else if (typeof state.response === "object") {
						state.popUpOpen = true;
						state.popUpMsg = "Customer group submitted successfully";
						state.popUpSeverity = "success";
					} else {
						state.popUpOpen = true;
						state.popUpMsg = "Threr is some error in submitting customer group";
						state.popUpSeverity = "error";
					}
					//state.popUpOpen = true;
					state.isLoading = false;
				}
			})
			.addCase(submitCustomerGroup.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from submitCustomerGroup", action.error.message);
			})

			.addCase(saveCustomerGroup.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(saveCustomerGroup.fulfilled, (state, action) => {
				state.isLoading = false;
				state.response = action.payload;
				if (state.isEdit === false) {
					if (typeof state.response === "string") {
						state.popUpMsg = state.response;
						state.popUpSeverity = "info";
					} else if (typeof state.response === "object") {
						state.popUpMsg = "Customer group saved successfully";
						state.popUpSeverity = "success";
					} else {
						state.popUpMsg = "Threr is some error in saving customer group";
						state.popUpSeverity = "error";
					}
					state.popUpOpen = true;
				} else {
					if (typeof state.response === "string") {
						state.popUpMsg = state.response;
						state.popUpSeverity = "info";
					} else if (typeof state.response === "object") {
						state.popUpMsg = "Customer group saved successfully";
						state.popUpSeverity = "success";
					} else {
						state.popUpMsg = "Threr is some error in saving customer group";
						state.popUpSeverity = "error";
					}
					state.popUpOpen = true;
				}
			})
			.addCase(saveCustomerGroup.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from saveCustomerGroup", action.error.message);
			})

			.addCase(cloneCustomerGroup.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(cloneCustomerGroup.fulfilled, (state, action) => {
				state.isLoading = false;
				state.response = action.payload;
				if (state.isEdit === false) {
					if (typeof state.response === "string") {
						state.popUpMsg = state.response;
						state.popUpSeverity = "info";
					} else if (typeof state.response === "object") {
						state.popUpMsg = "Customer group cloned successfully";
						state.popUpSeverity = "success";
					} else {
						state.popUpMsg = "Threr is some error in cloning customer group";
						state.popUpSeverity = "error";
					}
					state.popUpOpen = true;
				} else {
					if (typeof state.response === "string") {
						state.popUpMsg = state.response;
						state.popUpSeverity = "info";
					} else if (typeof state.response === "object") {
						state.popUpMsg = "Customer group cloned successfully";
						state.popUpSeverity = "success";
					} else {
						state.popUpMsg = "Threr is some error in cloning customer group";
						state.popUpSeverity = "error";
					}
					state.popUpOpen = true;
				}
			})
			.addCase(cloneCustomerGroup.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from cloneCustomerGroup", action.error.message);
			})

			.addCase(uploadFileForCustomerGroup.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(uploadFileForCustomerGroup.fulfilled, (state, action) => {
				//state.isLoading = false;
			})
			.addCase(uploadFileForCustomerGroup.rejected, (state, action) => {
				//state.isLoading = false;
				console.error(
					"Error from uploadFileForCustomerGroup",
					action.error.message
				);
			})

			.addCase(downloadAttachment.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(downloadAttachment.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(downloadAttachment.rejected, (state, action) => {
				state.isLoading = false;
				console.error("Error from downloadAttachment", action.error.message);
			});
	},
});

export const { setCGState, addFile, deleteFile, resetAllStates } =
	customerGroupSetupSlice.actions;

export default customerGroupSetupSlice.reducer;
